/**
 * ステージ選択(docs/02_game_design.md §1)。
 *
 * - カテゴリタブ(れんしゅう / トレーニング)
 * - 難易度トグル(かんたん / ふつう / むずかしい)は**全体設定**として保存され、
 *   星表示も選択中難易度のものに切り替わる
 * - 解放条件(§6.4)を満たさないステージは鍵つきで表示し、確認後に無料で解除できる
 *
 * 操作はキーだけでも完結する(docs/15 §2A):数字キーで直接えらび、
 * ←→↑↓ で移動、Enter で決定。カードには小さなキーバッジを出す。
 * カードは並び順にポン・ポンと出る(§2C)。
 */

import { useEffect, useRef, useState, type KeyboardEvent, type RefCallback } from 'react';
import type { Difficulty } from '@paltyping/engine';
import { groupBySection, isStageGraduated, nativeFiveStarClearsRemaining } from '../logic/unlock';
import type { StageCategory, StageDefinition } from '../services/content';
import { selectCategories, selectStageList, useGameStore } from '../store/gameStore';
import { DebugUnlockBadge, useDebugUnlockAll } from './components/DebugUnlockBadge';
import {
  CHOICE_KEYS,
  KeyBadge,
  popInDelay,
  useArrowFocus,
  useChoiceKeys,
} from './components/gameFeel';
import { ProfileBar } from './components/ProfileBar';
import { Stars } from './components/Stars';

const CATEGORY_LABELS: Readonly<Record<StageCategory, string>> = {
  practice: 'れんしゅう',
  training: 'トレーニング',
};

const DIFFICULTY_LABELS: readonly { readonly value: Difficulty; readonly label: string }[] = [
  { value: 'easy', label: 'かんたん' },
  { value: 'normal', label: 'ふつう' },
  { value: 'hard', label: 'むずかしい' },
];

const SECTION_LABELS: Readonly<Record<string, string>> = {
  'home-position': 'ホームポジション',
  gojuon: 'ごじゅうおん',
  dakuon: 'だくおん・はんだくおん',
  'small-kana': 'ちいさい もじ',
  'all-kana': 'すべての もじ',
  numbers: 'すうじ',
  time: 'とき・ようび',
  family: 'かぞく・ひと',
  animals: 'どうぶつ',
  food: 'たべもの',
  body: 'からだ',
  school: 'がっこう',
  town: 'まち',
  transport: 'のりもの',
  nature: 'しぜん',
  colors: 'いろ',
  feelings: 'きもち',
  verbs: 'うごきの ことば',
  adjectives: 'ようすの ことば',
  place: 'ばしょ',
  expressions: 'あいさつ',
  others: 'そのほか',
};

export function StageSelectScreen() {
  const state = useGameStore();
  // デバッグ解放の on/off で解放判定が変わるので、値の変化で再描画させる。
  useDebugUnlockAll();
  const items = selectStageList(state);
  const categories = selectCategories(state);
  const sections = groupBySection(items);
  const arrows = useArrowFocus<HTMLButtonElement>();
  const [stageToUnlock, setStageToUnlock] = useState<StageDefinition | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(false);
  const cardRefs = useRef(new Map<string, HTMLButtonElement>());

  const closeUnlockDialog = () => {
    if (unlocking) return;
    const stageId = stageToUnlock?.id;
    setStageToUnlock(null);
    setUnlockError(false);
    // ダイアログを閉じたら、開いたカードへ戻す(キーボード操作を見失わない)。
    queueMicrotask(() => stageId && cardRefs.current.get(stageId)?.focus());
  };

  useEffect(() => {
    if (!stageToUnlock) return;
    const dialog = document.querySelector<HTMLElement>('[data-unlock-dialog]');
    const focusable = () =>
      Array.from(
        dialog?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled])',
        ) ?? [],
      );
    focusable()[0]?.focus();

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      // IME 確定中の Enter はダイアログの操作にしない。
      if (event.isComposing || event.keyCode === 229) {
        event.stopPropagation();
        return;
      }
      if (event.key === 'Tab') {
        const nodes = focusable();
        if (nodes.length === 0) return;
        const current = nodes.indexOf(document.activeElement as HTMLElement);
        const next = event.shiftKey
          ? current <= 0
            ? nodes.length - 1
            : current - 1
          : current === nodes.length - 1
            ? 0
            : current + 1;
        event.preventDefault();
        nodes[next]?.focus();
      } else if (event.key === 'Escape' && !unlocking) {
        event.preventDefault();
        closeUnlockDialog();
      }
      // document で止めるので、背後の window ショートカットには届かない。
      event.stopPropagation();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [stageToUnlock, unlocking]);

  /*
   * キー操作とポップインの順番は「見出しをまたいだ通し番号」で決める
   * (画面に並んで見えるとおりの順 = 子の目の動きと一致する)。
   * 見出しごとに 1 に戻すと、同じ「1」が画面に何個も出てしまう。
   */
  const flat = sections.flatMap((section) => section.items);
  const orderOf = new Map(flat.map((entry, index) => [entry.stage.id, index]));

  const pickStage = (entry: (typeof flat)[number]) => {
    if (isStageGraduated(entry.stage.id, state.settings.difficulty, state.progress)) return;
    if (entry.unlocked) {
      void state.startStage(entry.stage.id);
      return;
    }
    setUnlockError(false);
    setStageToUnlock(entry.stage);
  };

  /** 数字キーでステージを直接えらぶ。通常ロックは確認を開く。 */
  useChoiceKeys(
    CHOICE_KEYS,
    (index) => {
      const entry = flat[index];
      if (entry) pickStage(entry);
    },
    stageToUnlock === null,
  );

  const confirmUnlock = async () => {
    if (!stageToUnlock || unlocking) return;
    setUnlocking(true);
    setUnlockError(false);
    const unlocked = await state.unlockStage(stageToUnlock.id);
    setUnlocking(false);
    if (unlocked) closeUnlockDialog();
    else setUnlockError(true);
  };

  const rejectImeEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'Enter' &&
      (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229)
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <div className="screen stage-select">
      <header className="stage-select-header">
        <button type="button" className="back-button" onClick={state.goModeSelect}>
          もどる
        </button>
        <h1 className="screen-title">
          {state.language === 'japanese' ? 'にほんご' : 'えいご'} の ステージ
        </h1>
        <p className="coin-line">
          <span className="coin-icon">◎</span> {state.progress.coins}
        </p>
      </header>

      <ProfileBar compact />

      <DebugUnlockBadge />

      <div className="toolbar">
        <div className="tabs" role="tablist" aria-label="カテゴリ">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              role="tab"
              aria-selected={state.category === category}
              className={`tab ${state.category === category ? 'on' : ''}`}
              onClick={() => state.setCategory(category)}
            >
              {CATEGORY_LABELS[category]}
            </button>
          ))}
        </div>

        <div className="difficulty-toggle" role="group" aria-label="むずかしさ">
          {DIFFICULTY_LABELS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`diff ${value} ${state.difficulty === value ? 'on' : ''}`}
              onClick={() => state.setDifficulty(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {state.stageStatus === 'loading' ? <p className="loading-text">よみこみちゅう…</p> : null}
      {state.stageStatus === 'error' ? (
        <p className="error-text">ステージを よみこめませんでした: {state.stageError}</p>
      ) : null}
      {state.stageStatus !== 'error' && state.stageError ? (
        <p className="error-text" role="alert">
          {state.stageError}
        </p>
      ) : null}

      <div className="stage-sections" onKeyDown={arrows.onKeyDown}>
        {sections.map((section) => (
          <section key={section.section} className="stage-section">
            <h2 className="section-title">{SECTION_LABELS[section.section] ?? section.section}</h2>
            <div className="stage-grid">
              {section.items.map(({ stage, unlocked, stars, bestScore }) => {
                // 通し番号(見出しをまたぐ)。キーもポップインの順もこれで決まる。
                const index = orderOf.get(stage.id) ?? 0;
                const key = CHOICE_KEYS[index];
                const graduated = isStageGraduated(
                  stage.id,
                  state.settings.difficulty,
                  state.progress,
                );
                const remaining = nativeFiveStarClearsRemaining(
                  stage,
                  state.settings.difficulty,
                  state.settings.firstLanguage,
                  state.progress,
                );
                return (
                  <button
                    key={stage.id}
                    type="button"
                    ref={
                      ((node) => {
                        arrows.register(index)(node);
                        if (node) cardRefs.current.set(stage.id, node);
                        else cardRefs.current.delete(stage.id);
                      }) as RefCallback<HTMLButtonElement>
                    }
                    className={`stage-card gf-pop-in ${graduated ? 'graduated' : unlocked ? '' : 'locked'}`}
                    data-stage-id={stage.id}
                    disabled={graduated}
                    // 順番ポップイン(docs/15 §2C)。transform / opacity だけ。
                    style={popInDelay(index)}
                    onClick={() => pickStage({ stage, unlocked, stars, bestScore })}
                  >
                    <span className="stage-name">{stage.title}</span>
                    {stage.titleSub ? <span className="stage-sub">{stage.titleSub}</span> : null}
                    <Stars value={stars} size="sm" />
                    <span className="stage-best">
                      {graduated
                        ? '🏅 そつぎょう'
                        : unlocked
                          ? bestScore > 0
                            ? `ベスト ${bestScore}`
                            : 'はじめて!'
                          : '🔒 まだ'}
                    </span>
                    {remaining !== null && !graduated ? (
                      <span className="stage-graduation-progress">★5 あと {remaining}かい</span>
                    ) : null}
                    {key ? <KeyBadge keyLabel={key} corner /> : null}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {items.length > 0 ? (
        <p className="gf-key-hint">すうじキー か ←→↑↓ と Enter でも えらべるよ</p>
      ) : null}

      {stageToUnlock ? (
        <div className="unlock-dialog-backdrop" role="presentation">
          <div
            className="unlock-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="unlock-dialog-title"
            data-unlock-dialog
            onKeyDown={rejectImeEnter}
          >
            <h2 id="unlock-dialog-title">ロックかいじょする？</h2>
            <p>
              <strong>{stageToUnlock.title}</strong> を むりょうで あそべるようにするよ。
            </p>
            {unlockError ? (
              <p className="unlock-dialog-error" role="alert">
                つうしんに しっぱいしたよ。もういちど ためしてね。
              </p>
            ) : null}
            <div className="unlock-dialog-actions">
              <button type="button" onClick={closeUnlockDialog} disabled={unlocking}>
                やめる
              </button>
              <button
                type="button"
                className="primary"
                onClick={() => void confirmUnlock()}
                disabled={unlocking}
              >
                {unlocking
                  ? 'かいじょ ちゅう…'
                  : unlockError
                    ? 'もういちど かいじょする'
                    : 'かいじょする'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
